/**
 * Console navigation.
 *
 * Pages come from docs/design/07-frontend.md; the grouping is what an operator
 * reaches for during an incident, not the order the docs list them in.
 */

export const NAV_GROUPS = ['monitor', 'pool', 'tools', 'access', 'operations'] as const
export type NavGroup = (typeof NAV_GROUPS)[number]

export type NavIconName =
  | 'gauge'
  | 'idCard'
  | 'globe'
  | 'key'
  | 'terminal'
  | 'plug'
  | 'link'
  | 'book'
  | 'list'
  | 'sliders'
  | 'bell'
  | 'server'
  | 'activity'
  | 'archive'
  | 'download'
  | 'clock'
  | 'users'
  | 'lock'

export interface NavItem {
  path: string
  /** Key in the console namespace. */
  labelKey: string
  group: NavGroup
  icon: NavIconName
  /**
   * Shown to the public demo account.
   *
   * Absent means no, which is the only safe default: a page added later is
   * hidden from a demo instance until somebody decides otherwise, rather than
   * being published to strangers because nobody remembered this field.
   *
   * The flag hides a link; it does not protect anything. The API refuses these
   * pages' endpoints for a demo caller on its own - see `demo_read` and
   * `read_admin_demo` in the backend - and this exists so a demo visitor sees a
   * console that works instead of a sidebar two thirds of which answers 403.
   */
  demo?: true
}

export const NAV_ITEMS: readonly NavItem[] = [
  { path: '/overview', labelKey: 'nav.overview', group: 'monitor', icon: 'gauge', demo: true },
  { path: '/identities', labelKey: 'nav.identities', group: 'pool', icon: 'idCard' },
  { path: '/proxies', labelKey: 'nav.proxies', group: 'pool', icon: 'globe' },
  { path: '/scheduler', labelKey: 'nav.scheduler', group: 'pool', icon: 'sliders', demo: true },
  { path: '/playground', labelKey: 'nav.playground', group: 'tools', icon: 'terminal', demo: true },
  { path: '/tools', labelKey: 'nav.tools', group: 'tools', icon: 'terminal', demo: true },
  { path: '/library', labelKey: 'nav.library', group: 'tools', icon: 'archive', demo: true },
  { path: '/watchlist', labelKey: 'nav.watchlist', group: 'tools', icon: 'clock' },
  { path: '/downloads', labelKey: 'nav.downloads', group: 'tools', icon: 'download', demo: true },
  { path: '/usage', labelKey: 'nav.usage', group: 'tools', icon: 'clock' },
  { path: '/transcriptions', labelKey: 'nav.transcriptions', group: 'tools', icon: 'book' },
  { path: '/docs', labelKey: 'nav.apiDocs', group: 'tools', icon: 'book', demo: true },
  // Not '/mcp': that path is the MCP endpoint itself, mounted on the API.
  { path: '/mcp-guide', labelKey: 'nav.mcp', group: 'tools', icon: 'plug', demo: true },
  { path: '/api-keys', labelKey: 'nav.apiKeys', group: 'access', icon: 'key', demo: true },
  { path: '/endpoint-access', labelKey: 'nav.endpointAccess', group: 'access', icon: 'lock' },
  { path: '/users', labelKey: 'nav.users', group: 'access', icon: 'users' },
  { path: '/logs', labelKey: 'nav.logs', group: 'operations', icon: 'list', demo: true },
  { path: '/system', labelKey: 'nav.system', group: 'operations', icon: 'server', demo: true },
  { path: '/diagnose', labelKey: 'nav.diagnose', group: 'operations', icon: 'activity' },
  { path: '/backup', labelKey: 'nav.backup', group: 'operations', icon: 'archive' },
  { path: '/notifications', labelKey: 'nav.notifications', group: 'operations', icon: 'bell' },
  { path: '/settings', labelKey: 'nav.settings', group: 'operations', icon: 'sliders' },
]

/**
 * The navigation a role may see.
 *
 * Only the demo account is filtered. Every other role reaches every page and is
 * refused per endpoint if it lacks the rank, which is the behaviour the console
 * has always had: an operator seeing a Users link and being told no is a useful
 * thing to learn about their own instance. A stranger on a public demo learns
 * nothing from a link that always fails, so theirs is trimmed instead.
 */
export function navItemsFor(role: string | null | undefined): readonly NavItem[] {
  return NAV_ITEMS.filter((item) => {
    if ((item.path === '/transcriptions' || item.path === '/usage') && (!role || role === 'demo')) return false
    return role !== 'demo' || item.demo
  })
}

/** Whether a role may open a page at all, for a directly typed URL. */
export function canOpen(role: string | null | undefined, path: string): boolean {
  if (
    path === '/usage' ||
    path === '/transcriptions' ||
    path.startsWith('/transcriptions/') ||
    isExtractPath(path)
  ) {
    return role === 'viewer' || role === 'operator' || role === 'admin'
  }
  if (role !== 'demo') return true
  if (PUBLIC_ROUTES.includes(path) || path === '/about') return true
  const item = navItemFor(path)
  return item?.demo === true
}

/** Routes reachable without a session. `/wanxiang` is a prefix, not one path. */
export const PUBLIC_ROUTES: readonly string[] = ['/login', '/setup', '/wanxiang']

export function isWanxiangPath(path: string): boolean {
  return path === '/wanxiang' || path.startsWith('/wanxiang/')
}

/** The top-level sections. Every console and public page shows these three. */
export const SITE_SECTIONS = [
  { id: 'wanxiang', href: '/wanxiang', labelKey: 'site.wanxiang' },
  { id: 'extract', href: '/extract', labelKey: 'site.extract' },
  { id: 'admin', href: '/overview', labelKey: 'site.admin' },
] as const

export type SiteSection = (typeof SITE_SECTIONS)[number]['id']

/** `/extract` is login-only; its children are one job and its comparison view. */
export function isExtractPath(path: string): boolean {
  return path === '/extract' || path.startsWith('/extract/')
}

/** Which top-level tab a path belongs to. Anything unknown counts as admin. */
export function siteSectionFor(path: string): SiteSection {
  if (isWanxiangPath(path)) return 'wanxiang'
  if (isExtractPath(path)) return 'extract'
  return 'admin'
}

/** The post-login landing page: the daily-use extraction screen. */
export const DEFAULT_HOME = '/extract'

/** Only return to known console pages, never an external URL or a login loop. */
export function safeReturnTo(value: string | null | undefined): string {
  const unsafeCharacters = [...(value ?? '')].some(
    (character) => character === '\\' || character.charCodeAt(0) <= 32 || character.charCodeAt(0) === 127,
  )
  if (!value || !value.startsWith('/') || value.startsWith('//') || unsafeCharacters) {
    return DEFAULT_HOME
  }
  try {
    const url = new URL(value, 'https://console.invalid')
    const knownPage = NAV_ITEMS.some((item) => item.path === url.pathname)
      || url.pathname === '/about'
      || url.pathname === '/parse'
      || url.pathname === '/extract'
      || /^\/extract\/[a-zA-Z0-9_-]+(?:\/compare)?$/.test(url.pathname)
      || /^\/transcriptions\/[a-zA-Z0-9_-]+(?:\/compare)?$/.test(url.pathname)
    if (url.origin !== 'https://console.invalid' || !knownPage) return DEFAULT_HOME
    return `${url.pathname}${url.search}${url.hash}`
  } catch {
    return DEFAULT_HOME
  }
}

export function loginHref(returnTo: string): string {
  const destination = safeReturnTo(returnTo)
  return destination === DEFAULT_HOME ? '/login' : `/login?next=${encodeURIComponent(destination)}`
}

/**
 * Pages that are real but not in the navigation groups.
 *
 * About is the colophon: it has its own link pinned under the nav rather than
 * a place in a group, and the breadcrumb still has to be able to name it -
 * without this it resolved to "page not found" on a page that had just
 * rendered.
 */
const UNLISTED: readonly NavItem[] = [
  { path: '/about', labelKey: 'page.about.title', group: 'operations', icon: 'server' },
]

/**
 * The extraction screen is not in the sidebar on purpose: it is a top-level tab
 * with its own shell. It still needs a title for the document and a breadcrumb
 * label if a future console page links to it.
 */
const EXTRACT_ITEM: NavItem = {
  path: '/extract',
  labelKey: 'page.extract.title',
  group: 'tools',
  icon: 'download',
}

export function navItemFor(path: string): NavItem | undefined {
  if (isExtractPath(path)) {
    return /^\/extract\/[^/]+\/compare$/.test(path)
      ? { ...EXTRACT_ITEM, path, labelKey: 'compare.title' }
      : { ...EXTRACT_ITEM, path }
  }
  if (/^\/transcriptions\/[^/]+\/compare$/.test(path)) return { path, labelKey: 'compare.title', group: 'tools', icon: 'book' }
  if (path === '/') return navItemFor(DEFAULT_HOME)
  return [...NAV_ITEMS, ...UNLISTED].find(
    (item) => path === item.path || path.startsWith(`${item.path}/`),
  )
}
